import { Box } from '@mui/material';
import { MiniLoader } from '@pwc/pwc-deals-ui';
import SupportTicketLink from 'common/components/SupportTicketLink';
import { useMemo, VFC } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageBox } from '../MessageBox';
import {
  LibraryItemType,
  LibraryItemTypeEnum,
} from './AsyncSharingStatusMessage';

const InProgressMessages = {
  TABLEAU_TITLE_PWC_USER: 'cloningTableauFileForClient',
  TABLEAU_TITLE_CLIENT_USER: 'preparingTableauFile',
  POWER_PIVOT_TITLE_PWC_USER: 'cloningExcelFileForClient',
  POWER_PIVOT_TITLE_CLIENT_USER: 'preparingExcelFile',
};

type Props = {
  isPwcUser: boolean;
  type: LibraryItemType;
};

const AsyncInProgressStatusMessage: VFC<Props> = ({ isPwcUser, type }) => {
  const { t } = useTranslation();
  const title = useMemo(() => {
    if (type === LibraryItemTypeEnum.Tableau) {
      if (isPwcUser) {
        return t(InProgressMessages.TABLEAU_TITLE_PWC_USER);
      }
      return t(InProgressMessages.TABLEAU_TITLE_CLIENT_USER);
    }
    if (type === LibraryItemTypeEnum.PowerPivot) {
      if (isPwcUser) {
        return t(InProgressMessages.POWER_PIVOT_TITLE_PWC_USER);
      }
      return t(InProgressMessages.POWER_PIVOT_TITLE_CLIENT_USER);
    }
    return '';
  }, [isPwcUser, type, t]);

  const message = useMemo(() => {
    if (isPwcUser) {
      return (
        <>
          {t('checkIfFileIsReady')}{' '}
          <SupportTicketLink>{t('createSupportTicket')}</SupportTicketLink>{' '}
          {t('forMoreHelp')}
        </>
      );
    }

    return <>{t('takeFewMinutesCompleteRefresh')}</>;
  }, [isPwcUser, t]);

  return (
    <MessageBox>
      <Box
        sx={{ display: 'flex', alignItems: 'center', mb: '1rem' }}
        data-testid="async-sharing-status-message-in-progress"
      >
        <Box
          sx={{
            '& svg': {
              mt: '-3px',
              width: '24px',
              height: '24px',
            },
          }}
        >
          <MiniLoader />
        </Box>
        <Box sx={{ ml: '0.75rem', fontSize: '1.125rem', fontWeight: 'bold' }}>
          {title}
        </Box>
      </Box>
      <Box data-testid="async-sharing-status-message__content-in-progress">
        {message}
      </Box>
    </MessageBox>
  );
};

export default AsyncInProgressStatusMessage;
